import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  TableContainer,
  Typography,
} from '@material-ui/core'
import {
  DataGrid,
  GridCellEditCommitParams,
  GridColDef,
  GridEditRowsModel,
  GridRowData,
  GridRowParams,
} from '@mui/x-data-grid'
import React, { useEffect, useRef, useState } from 'react'
import { Copy, Edit3 } from 'react-feather'
import { Control, FieldErrors } from 'react-hook-form'
import {
  formatTotalAirFlowPerArea,
  temperatureUnit,
} from '../../../../util/formatters'
import { validators } from '../../../../validation/validators'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { DeviceData, deviceDefaultValues, FormValues } from '../form-values'
import { theme } from '../../../../theme'
import { ScheduleFragment } from '../../../../generated/graphql'
import { t } from '../../../../i18n'
import { VentilationDeviceDialog } from './VentilationDeviceDialog'
import { VentilationFormGridFooter } from './VentilationFormGridFooter'
import { getDataGridLocale } from '../../../../util/data-grid-util'
import { parseDecimal } from '../../../../util/parsers'

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZE_OPTIONS = [5, 10, 15]
const TABLE_WIDTH = 1300

const useStyles = makeStyles({
  gridContainer: {
    flexGrow: 1,
    width: '100%',
    maxWidth: TABLE_WIDTH,
    height: '500px',
  },
})

const useDataGridStyles = makeStyles(() => ({
  root: {
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .Mui-error': {
      backgroundColor: `rgb(126,10,15,0.2)`,
    },
  },
}))

type Props = {
  buildingEnergyModelArea: number
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  enableSaving: (enable: boolean) => void
}

export function VentilationForm({
  buildingEnergyModelArea,
  schedules,
  control,
  errors,
  enableSaving,
}: Props): React.ReactElement {
  const [page, setPage] = useState(0) // eslint-disable-line
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE) // eslint-disable-line
  const [currentDevices, setCurrentDevices] = useState<DeviceData[]>([])

  const [isCreatingDevice, setIsCreatingDevice] = useState(false)
  const [editingDevice, setEditingDevice] = useState<DeviceData | undefined>()

  const [editRowsModel, setEditRowsModel] = React.useState<GridEditRowsModel>(
    {},
  )

  // This ref is used due to validation issues with singleselect type in editable datagrid
  const editRowsModelRef = useRef<GridEditRowsModel>({})

  const selectedDeviceRef = useRef(0)

  const classes = useStyles()
  const gridClasses = useDataGridStyles()

  const onDialogClose = () => {
    setIsCreatingDevice(false)
    setEditingDevice(undefined)
  }

  const onCreateStart = () => setIsCreatingDevice(true)

  const startEdit = () => {
    setEditingDevice(currentDevices[selectedDeviceRef.current])
  }

  const initDevices = () => {
    setCurrentDevices(
      control.defaultValuesRef.current.parameters?.ventilation
        ?.deviceData as DeviceData[],
    )
  }

  const addDevice = (device: DeviceData) => {
    setCurrentDevices([...currentDevices, device])
    setIsCreatingDevice(false)
  }

  const editDevice = (device: DeviceData, deviceIndex = -1) => {
    const tempDevices = [...currentDevices]
    if (deviceIndex < 0) tempDevices[selectedDeviceRef.current] = { ...device }
    else tempDevices[deviceIndex] = { ...device }
    setCurrentDevices([...tempDevices])
    setEditingDevice(undefined)
  }

  const copyDevice = () => {
    const device = { ...currentDevices[selectedDeviceRef.current] }
    device.deviceName += ' - Copy'
    setCurrentDevices([...currentDevices, device])
  }

  const onCreateDeviceSubmit = (values: DeviceData) => {
    if (isCreatingDevice) addDevice(values)
    else editDevice(values)
  }

  const onDeleteDevice = () => {
    const tempDevices = [...currentDevices]
    tempDevices.splice(selectedDeviceRef.current, 1)
    setCurrentDevices([...tempDevices])
    selectedDeviceRef.current = 0
    setEditingDevice(undefined)
  }

  const onDeviceDialogClose = () => {
    setIsCreatingDevice(false)
    setEditingDevice(undefined)
  }

  const calculateTotalAirFlow = (): string => {
    let sumAirFlow = 0
    if (currentDevices && currentDevices.length > 0) {
      sumAirFlow = currentDevices
        .map((d) => parseDecimal(d.airFlow))
        .reduce((a, b) => a + b)
    }
    return formatTotalAirFlowPerArea(sumAirFlow / buildingEnergyModelArea)
  }

  useEffect(() => {
    currentDevices.forEach((device, index) => {
      control.setValue(
        `parameters.ventilation.deviceData.${index}.deviceName`,
        device.deviceName,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.SFP`,
        device.SFP,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.airFlow`,
        device.airFlow,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.chilldrop`,
        device.chilldrop,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.chilldropTempLimit`,
        device.chilldropTempLimit,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.coolingCoil`,
        device.coolingCoil,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.demandBasedUsageCoefficient`,
        device.demandBasedUsageCoefficient,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.deviceScheduleId`,
        device.deviceScheduleId ?? '',
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.effectedArea`,
        device.effectedArea,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.exhaustAirHeatPump`,
        device.exhaustAirHeatPump,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.exhaustAirMinimumTemp`,
        device.exhaustAirMinimumTemp,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.heatRecoveryEfficiency`,
        device.heatRecoveryEfficiency,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.indoorAirTemp`,
        device.indoorAirTemp,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.returnAirTemp`,
        device.returnAirTemp,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.supplyAirTemp`,
        device.supplyAirTemp,
      )
      control.setValue(
        `parameters.ventilation.deviceData.${index}.systemType`,
        device.systemType,
      )
    })

    enableSaving(currentDevices && currentDevices.length > 0)
  }, [currentDevices, control, enableSaving])

  useEffect(() => {
    initDevices()
  }, []) // eslint-disable-line

  enum VentilationDeviceKeys {
    Id = 'id',
    Name = 'deviceName',
    EffectedArea = 'effectedArea',
    AirFlow = 'airFlow',
    UsageProfile = 'usageProfile',
    HeatRecoveryEfficiency = 'heatRecoveryEfficiency',
    SFP = 'SFP',
    Actions = 'actions',
  }

  const keyTranslations: Record<VentilationDeviceKeys, string> = {
    id: 'Id',
    deviceName: t('energySystem.parameters.ventilation.deviceName'),
    effectedArea: t('energySystem.parameters.ventilation.effectedArea'),
    airFlow: t('energySystem.parameters.ventilation.airFlow'),
    usageProfile: t('energySystem.parameters.ventilation.usageProfile'),
    heatRecoveryEfficiency: t(
      'energySystem.parameters.ventilation.heatRecoveryEfficiency',
    ),
    SFP: t('energySystem.parameters.ventilation.SFP'),
    actions: t('energySystem.parameters.ventilation.dialog.actions'),
  }

  const createColumn = (
    field: VentilationDeviceKeys,
    width: number,
    editable = false,
    type = 'string',
    flex?: number,
    hide = false,
  ): GridColDef => ({
    field,
    headerName: keyTranslations[field],
    width,
    editable,
    type,
    flex,
    sortable: false,
    hide,
  })

  const columns: GridColDef[] = [
    createColumn(VentilationDeviceKeys.Name, 150, true),
    createColumn(VentilationDeviceKeys.EffectedArea, 180, true),
    createColumn(VentilationDeviceKeys.AirFlow, 100, true, 'number'),
    // createColumn(VentilationDeviceKeys.UsageProfile, 170, true),
    {
      field: VentilationDeviceKeys.UsageProfile,
      headerName: keyTranslations[VentilationDeviceKeys.UsageProfile],
      width: 170,
      editable: true,
      type: 'singleSelect',
      valueOptions: schedules.map((s) => s.name),
      sortable: false,
    },
    createColumn(
      VentilationDeviceKeys.HeatRecoveryEfficiency,
      90,
      true,
      'number',
    ),
    createColumn(VentilationDeviceKeys.SFP, 90, true, 'number'),
    {
      field: 'Actions',
      headerName: keyTranslations[VentilationDeviceKeys.Actions],
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            aria-label="Modify"
            onClick={() => {
              selectedDeviceRef.current = Number(params.id.toString())
              startEdit()
            }}
          >
            <Edit3 size={20} />
          </IconButton>
          <IconButton
            color="primary"
            aria-label="Modify"
            onClick={() => {
              selectedDeviceRef.current = Number(params.id.toString())
              copyDevice()
            }}
          >
            <Copy size={20} />
          </IconButton>
        </>
      ),
      sortable: false,
    },
    // createColumn(VentilationDeviceKeys.Id, 0, undefined, true),
  ]

  const handleRowClick = (param: GridRowParams) => {
    selectedDeviceRef.current = Number(param.id.toString())
  }

  const handleCellEditCommit = (params: GridCellEditCommitParams) => {
    const rowIndex = Number(params.id.toString())
    const device = { ...currentDevices[rowIndex] } as DeviceData
    switch (params.field) {
      case 'deviceName':
        device.deviceName = String(params.value)
        break
      case 'effectedArea':
        device.effectedArea = String(params.value)
        break
      case 'airFlow':
        device.airFlow = String(params.value)
        break
      case 'usageProfile':
        // device.deviceScheduleId = schedules.find(
        //   (s) => s.name === String(params.value),
        // )?.id

        device.deviceScheduleId = schedules.find(
          (s) =>
            s.name ===
            String(
              editRowsModelRef.current[params.id.toString()].usageProfile.value,
            ),
        )?.id
        break
      case 'heatRecoveryEfficiency':
        device.heatRecoveryEfficiency = String(params.value)
        break
      case 'SFP':
        device.SFP = String(params.value)
        break
      default:
        break
    }

    editDevice(device, rowIndex)
  }

  const handleEditRowsModelChange = React.useCallback(
    (newModel: GridEditRowsModel) => {
      const updatedModel = { ...newModel }

      const firstKey = Object.keys(updatedModel)[0]

      if (firstKey) {
        const propName = Object.keys(updatedModel[firstKey])[0]

        let validFunc = (_num: number) => true

        let isNumber = true

        switch (propName) {
          case 'airFlow':
            validFunc = (num: number) => num >= 0
            break
          case 'heatRecoveryEfficiency':
            validFunc = (num: number) => num >= 0 && num <= 1
            break
          case 'SFP':
            validFunc = (num: number) => num >= 0
            break
          default:
            isNumber = false
            break
        }

        if (updatedModel[firstKey][propName]) {
          let isValid = true
          if (isNumber)
            isValid = validFunc(
              updatedModel[firstKey][propName].value as number,
            )
          if (!isValid)
            updatedModel[firstKey][propName] = {
              ...updatedModel[firstKey][propName],
              error: !isValid,
            }
        }
      }

      editRowsModelRef.current = updatedModel

      setEditRowsModel(updatedModel)
    },
    [],
  )

  const gridRows: GridRowData[] = []

  const rowsWithId = () => {
    let i = 0
    currentDevices.forEach((d) => {
      const rowData = { id: i, ...d } as GridRowData
      rowData[VentilationDeviceKeys.UsageProfile] = d.deviceScheduleId
        ? schedules.find((s) => s.id === d.deviceScheduleId)?.name
        : ''

      gridRows.push(rowData)
      i += 1
    })
    return gridRows
  }

  if (currentDevices) rowsWithId()

  return (
    <>
      <TableContainer component={Paper} className={classes.gridContainer}>
        <DataGrid
          className={gridClasses.root}
          columns={columns}
          rows={gridRows}
          pageSize={pageSize}
          localeText={getDataGridLocale(pageSize)}
          page={page}
          disableColumnMenu
          hideFooterSelectedRowCount
          onRowClick={handleRowClick}
          onCellEditCommit={handleCellEditCommit}
          editRowsModel={editRowsModel}
          onEditRowsModelChange={handleEditRowsModelChange}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          components={{
            Footer: () =>
              VentilationFormGridFooter({
                onDeviceCreateStart: onCreateStart,
                totalAirFlowLabel: `${t(
                  'energySystem.parameters.ventilation.totalSpecificAirFlow',
                )} ${calculateTotalAirFlow()}`,
              }),
          }}
        />
      </TableContainer>

      <FormGroup style={{ marginTop: theme.spacing(3) }}>
        <Box display="none">
          {currentDevices.map((device, index) => (
            <Grid key={`device-${index.toString()}`}>
              <ControlledTextField
                key={`deviceName-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.deviceName`}
              />
              <ControlledTextField
                key={`effectedArea-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.effectedArea`}
              />
              <ControlledTextField
                key={`airFlow-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.airFlow`}
              />
              <ControlledTextField
                key={`deviceScheduleId-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.deviceScheduleId`}
              />
              <ControlledTextField
                key={`heatRecoveryEfficiency-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.heatRecoveryEfficiency`}
              />
              <ControlledTextField
                key={`SFP-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.SFP`}
              />
              <ControlledTextField
                key={`systemType-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.systemType`}
              />
              <ControlledTextField
                key={`demandBasedUsageCoefficient-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.demandBasedUsageCoefficient`}
              />
              <ControlledTextField
                key={`exhaustAirMinimumTemp-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.exhaustAirMinimumTemp`}
              />
              <ControlledTextField
                key={`coolingCoil-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.coolingCoil`}
              />
              <ControlledTextField
                key={`supplyAirTemp-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.supplyAirTemp`}
              />
              <ControlledTextField
                key={`indoorAirTemp-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.indoorAirTemp`}
              />
              <ControlledTextField
                key={`returnAirTemp-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.returnAirTemp`}
              />
              <ControlledTextField
                key={`chilldrop-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.chilldrop`}
              />
              <ControlledTextField
                key={`chilldropTempLimit-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.chilldropTempLimit`}
              />
              <ControlledTextField
                key={`exhaustAirHeatPump-${index.toString()}`}
                control={control}
                name={`parameters.ventilation.deviceData.${index}.exhaustAirHeatPump`}
              />
            </Grid>
          ))}
        </Box>

        <Grid container key="general" item alignItems="center" spacing={1}>
          <Grid item xs={6}>
            <ControlledTextField
              control={control}
              name="parameters.ventilation.heatingDistributionEfficiency"
              error={
                errors.parameters?.ventilation?.heatingDistributionEfficiency
              }
              label={t(
                'energySystem.parameters.ventilation.heatingDistributionEfficiency',
              )}
              rules={{ ...validators.required, ...validators.efficiency }}
              defaultValue="1"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              control={control}
              name="parameters.ventilation.heatPumpExhaustAirMinimumTemp"
              error={
                errors.parameters?.ventilation?.heatPumpExhaustAirMinimumTemp
              }
              label={t(
                'energySystem.parameters.ventilation.heatPumpExhaustAirMinimumTemp',
              )}
              rules={{ ...validators.required, ...validators.decimal }}
              InputProps={{
                endAdornment: <Typography>{temperatureUnit}</Typography>,
              }}
              defaultValue="0"
            />
          </Grid>
        </Grid>
      </FormGroup>

      {isCreatingDevice && (
        <VentilationDeviceDialog
          open={isCreatingDevice}
          onClose={onDialogClose}
          onSubmit={onCreateDeviceSubmit}
          defaultValues={deviceDefaultValues}
          isCreate
          schedules={schedules}
        />
      )}

      {editingDevice !== undefined && (
        <VentilationDeviceDialog
          open={editingDevice !== undefined}
          onClose={onDeviceDialogClose}
          onSubmit={onCreateDeviceSubmit}
          defaultValues={editingDevice}
          isCreate={false}
          schedules={schedules}
          onDelete={onDeleteDevice}
        />
      )}
    </>
  )
}
